import {
  TextField,
  Button,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  Switch,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import FormFieldErrorClass from "./models/formFieldError";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ErrorIcon from "@mui/icons-material/Error";
import { useState, useEffect } from "react";
import {
  FormContainer,
  FormSubmitError,
  ActionButtonsContainer,
  InputFieldsWrapper,
  HeaderNote,
} from "./shared";
import styled from "styled-components";
import colors from "../../theme/colors";
import { useDispatch, useSelector } from "react-redux";
import {
  add as addNotification,
  remove as removeNotification,
} from "../../redux-store/notificationsSlice";
import isNumeric from "validator/lib/isNumeric";
import { FixedTwoDecimalPlaces } from "../../utils/numbers";
import { formatDateTimeStamp } from "../../utils/date";
import { InfoIcon, TrashIcon } from "../../theme/icons";

const SubSectionHeader = styled.div`
  font-size: 16px;
  border-bottom: 1px solid ${colors.gray};
  margin: 20px 0;
  font-weight: 500;
  &.no-bottom-margin {
    margin: 20px 0 0 0;
  }
`;

const IconWrapper = styled.div`
  display: flex;
`;

const ErrorContainer = styled.div`
  margin: 4px 0 12px 0;
`;

const ActionButtonsContainerWithSpace = styled(ActionButtonsContainer)`
  margin: 35px 0 60px;
`;

const InlineToolTip = styled.div`
  font-size: 12px;
`;

const DetailSubHeader = styled.div`
  font-size: 12px;
  margin-bottom: 12px;
`;

const CustomToggleFieldContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DeviceRowWrapper = styled.div`
  display: flex;
  align-item: center;
  justify-content: center;
  gap: 10px;
`;

const DeviceRowNumber = styled.div`
  display: flex;
  align-items: center;
  width: 25px;
  font-size: 12px;
`;

class DeviceClass {
  constructor(particleDeviceId) {
    this.particleDeviceId = particleDeviceId;
    this.error = new FormFieldErrorClass();
  }
}

const AdminDeviceSettingsForm = ({
  handleCancel = () => {},
  handleSave = () => {},
  saving = false,
  error = "",
  device = {},
}) => {
  const dispatch = useDispatch();

  //UX State
  const [save, setSave] = useState(false);

  //Form Fields
  const [externalTempEnabled, setExternalTempEnabled] = useState(true);
  const [volumeEnabled, setVolumeEnabled] = useState(true);
  const [rateOfLossAlarmEnabled, setRateOfLossAlarmEnabled] = useState(true);
  const [dataPublishingIntervalMinutes, setdataPublishingIntervalMinutes] =
    useState("3600");
  const [readSensorsIntervalMinutes, setreadSensorsIntervalMinutes] =
    useState("900");

  const [permanentLongModeEnabled, setPermanentLongModeEnabled] =
    useState(false);

  const [
    rateOfLossAlarmAlgoWindowSeconds,
    setRateOfLossAlarmAlgoWindowSeconds,
  ] = useState("1800");

  //Error State
  const [externalTempEnabledError, setExternalTempEnabledError] = useState(
    new FormFieldErrorClass(),
  );
  const [volumeEnabledError, setVolumeEnabledError] = useState(
    new FormFieldErrorClass(),
  );
  const [rateOfLossAlarmEnabledError, setRateOfLossAlarmEnabledError] =
    useState(new FormFieldErrorClass());

  const [dataPublishingIntervalError, setDataPublishingIntervalError] =
    useState(new FormFieldErrorClass());

  const [readSensorsIntervalError, setReadSensorsIntervalError] = useState(
    new FormFieldErrorClass(),
  );

  const [permanentLongModeEnabledError, setPermanentLongModeEnabledError] =
    useState(new FormFieldErrorClass());

  const [
    rateOfLossAlarmAlgoWindowSecondsError,
    setRateOfLossAlarmAlgoWindowSecondsError,
  ] = useState(new FormFieldErrorClass());

  const formFieldErrors = [
    externalTempEnabledError,
    volumeEnabledError,
    rateOfLossAlarmEnabledError,
    dataPublishingIntervalError,
    readSensorsIntervalError,
    rateOfLossAlarmAlgoWindowSecondsError,
    permanentLongModeEnabledError,
  ];

  useEffect(() => {
    if (device?.deviceSettings?.externalTempEnabled !== undefined) {
      const {
        externalTempEnabled,
        volumeEnabled,
        rateOfLossAlarmEnabled,
        dataPublishingIntervalMinutes,
        readSensorsIntervalMinutes,
        rateOfLossAlarmAlgoWindowSeconds,
        permanentLongModeEnabled,
      } = device.deviceSettings;
      setExternalTempEnabled(externalTempEnabled);
      setVolumeEnabled(volumeEnabled);
      setRateOfLossAlarmEnabled(rateOfLossAlarmEnabled);
      setdataPublishingIntervalMinutes(dataPublishingIntervalMinutes);
      setreadSensorsIntervalMinutes(readSensorsIntervalMinutes);
      setRateOfLossAlarmAlgoWindowSeconds(rateOfLossAlarmAlgoWindowSeconds);
      setPermanentLongModeEnabled(permanentLongModeEnabled);
    }
  }, [device]);

  useEffect(() => {
    if (save) {
      setSave(false);
      if (formFieldErrors.filter((e) => e.hasError).length) {
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Form Error",
            message: "Please check all form inputs for errors",
          }),
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 4000);
        return;
      }

      // send data
      handleSave({
        deviceId: device.id,
        externalTempEnabled,
        volumeEnabled,
        rateOfLossAlarmEnabled,
        dataPublishingIntervalMinutes,
        readSensorsIntervalMinutes,
        rateOfLossAlarmAlgoWindowSeconds,
        permanentLongModeEnabled,
      });
    }
  }, [save]);

  const validate = {
    externalTempEnabled: (value) => {
      setExternalTempEnabledError({
        hasError: false,
        message: "",
      });
      if (typeof value !== "boolean") {
        setExternalTempEnabledError({
          hasError: true,
          message: "External Temperature Enabled Value Required",
        });
        return;
      }
    },
    volumeEnabled: (value) => {
      setVolumeEnabledError({
        hasError: false,
        message: "",
      });
      if (typeof value !== "boolean") {
        setVolumeEnabledError({
          hasError: true,
          message: "Device Volume Enabled Value Required",
        });
        return;
      }
    },
    rateOfLossAlarmEnabled: (value) => {
      setRateOfLossAlarmEnabledError({
        hasError: false,
        message: "",
      });
      if (typeof value !== "boolean") {
        setRateOfLossAlarmEnabledError({
          hasError: true,
          message: "Rate of Loss Alarm Enabled Value Required",
        });
        return;
      }
    },
    dataPublishingInterval: (value) => {
      setDataPublishingIntervalError({
        hasError: false,
        message: "",
      });
    },
    readSensorsInterval: (value) => {
      setReadSensorsIntervalError({
        hasError: false,
        message: "",
      });
    },
    rateOfLossAlarmAlgoWindowSeconds: (value) => {
      setRateOfLossAlarmAlgoWindowSecondsError({
        hasError: false,
        message: "",
      });
    },
    permanentLongModeEnabled: (value) => {
      setPermanentLongModeEnabledError({
        hasError: false,
        message: "",
      });
    },
  };

  const handlers = {
    externalTempEnabledChange: ({ target }) => {
      const { checked } = target;
      validate.externalTempEnabled(checked);
      setExternalTempEnabled(checked);
    },
    volumeEnabledChange: ({ target }) => {
      const { checked } = target;
      validate.volumeEnabled(checked);
      setVolumeEnabled(checked);
    },
    rateOfLossAlarmEnabledChange: ({ target }) => {
      const { checked } = target;
      validate.rateOfLossAlarmEnabled(checked);
      setRateOfLossAlarmEnabled(checked);
    },

    dataPublishingIntervalChange: ({ target }) => {
      const { value } = target;
      validate.dataPublishingInterval(value);
      setdataPublishingIntervalMinutes(value);
    },

    readSensorsIntervalChange: ({ target }) => {
      const { value } = target;
      validate.readSensorsInterval(value);
      setreadSensorsIntervalMinutes(value);
    },
    rateOfLossAlarmAlgoWindowChange: ({ target }) => {
      const { value } = target;
      validate.rateOfLossAlarmAlgoWindowSeconds(value);
      setRateOfLossAlarmAlgoWindowSeconds(value);
    },
    permanentLongModeEnabledChange: ({ target }) => {
      const { checked } = target;
      validate.permanentLongModeEnabled(checked);
      setPermanentLongModeEnabled(checked);
    },

    save: () => {
      //validate all
      validate.externalTempEnabled(externalTempEnabled);
      validate.volumeEnabled(volumeEnabled);
      validate.rateOfLossAlarmEnabled(rateOfLossAlarmEnabled);
      validate.dataPublishingInterval(dataPublishingIntervalMinutes);
      validate.readSensorsInterval(readSensorsIntervalMinutes);
      validate.rateOfLossAlarmAlgoWindowSeconds(
        rateOfLossAlarmAlgoWindowSeconds,
      );
      validate.permanentLongModeEnabled(permanentLongModeEnabled);
      setSave(true);
    },
    cancel: () => {
      handleCancel();
    },
  };

  return (
    <>
      <FormContainer>
        <InputFieldsWrapper>
          <InlineToolTip>
            Device Settings are updated on the physical device the next time the
            device comes online. In the event a device is currently online when
            updates are made it will be updated immediately.
          </InlineToolTip>
          <CustomToggleFieldContainer>
            <FormControlLabel
              id="external-temp-enabled"
              sx={{
                margin: "0",
                width: " 300px",
                justifyContent: "space-between",
              }}
              labelPlacement="start"
              control={<Switch checked={externalTempEnabled} />}
              label="External Temperature Enabled"
              onChange={handlers.externalTempEnabledChange}
            />
            <Tooltip
              enterTouchDelay={10}
              title="Enables External Temperature reading on a tank. This will be displayed to customers and used to trigger Low Temperature Alarms."
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>

          {externalTempEnabledError.hasError && (
            <ErrorContainer>
              <FormHelperText error>
                {externalTempEnabledError.message}
              </FormHelperText>
            </ErrorContainer>
          )}

          <CustomToggleFieldContainer>
            <FormControlLabel
              id="device-volume-enabled"
              sx={{
                margin: "0",
                width: " 300px",
                justifyContent: "space-between",
              }}
              labelPlacement="start"
              control={<Switch checked={volumeEnabled} />}
              label="Device Volume Enabled"
              onChange={handlers.volumeEnabledChange}
            />
            <Tooltip
              enterTouchDelay={10}
              title="Enables audible device alarm volume. When disabled, audible device alarm will be silenced."
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>

          {volumeEnabledError.hasError && (
            <ErrorContainer>
              <FormHelperText error>
                {volumeEnabledError.message}
              </FormHelperText>
            </ErrorContainer>
          )}

          <CustomToggleFieldContainer>
            <FormControlLabel
              id="rate-of-loss-alarm-enabled"
              sx={{
                margin: "0",
                width: " 300px",
                justifyContent: "space-between",
              }}
              labelPlacement="start"
              control={<Switch checked={rateOfLossAlarmEnabled} />}
              label="Rate of Loss Alarm Enabled"
              onChange={handlers.rateOfLossAlarmEnabledChange}
            />
            <Tooltip
              enterTouchDelay={10}
              title="Enables the Rate of Loss alarm. When disabled, Rate of Loss alarm will NOT be sent to the software and customers will NOT be alarmed."
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>

          {rateOfLossAlarmEnabledError.hasError && (
            <ErrorContainer>
              <FormHelperText error>
                {rateOfLossAlarmEnabledError.message}
              </FormHelperText>
            </ErrorContainer>
          )}

          <CustomToggleFieldContainer>
            <FormControlLabel
              id="permanent-long-mode-enabled"
              sx={{
                margin: "0",
                width: " 300px",
                justifyContent: "space-between",
              }}
              labelPlacement="start"
              control={<Switch checked={permanentLongModeEnabled} />}
              label="Permanent Long Mode Enabled"
              onChange={handlers.permanentLongModeEnabledChange}
            />
            <Tooltip
              enterTouchDelay={10}
              title="When enabled, the device will be in “always on” mode to continuously collect and push data without sleeping and waking up."
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>

          {permanentLongModeEnabledError.hasError && (
            <ErrorContainer>
              <FormHelperText error>
                {permanentLongModeEnabledError.message}
              </FormHelperText>
            </ErrorContainer>
          )}

          {/* Data Publishing Interval */}
          <CustomToggleFieldContainer>
            <TextField
              sx={{ width: "100%" }}
              id="data-publishing-interval"
              select
              variant="outlined"
              label="Data Publishing Interval"
              value={dataPublishingIntervalMinutes}
              helperText={dataPublishingIntervalError.message}
              error={dataPublishingIntervalError.hasError}
              slotProps={{
                select: {
                  native: true,
                },
              }}
              onChange={handlers.dataPublishingIntervalChange}
            >
              <option key={"60"} value={"60"}>
                1 hour
              </option>
              <option key={"120"} value={"120"}>
                2 hour
              </option>
              <option key={"180"} value={"180"}>
                3 hour
              </option>
            </TextField>
            <Tooltip
              enterTouchDelay={10}
              title="This is the frequency at which data is published to the Boreas Portal.
"
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>

          {/* Read Sensors Interval */}
          <CustomToggleFieldContainer>
            <TextField
              sx={{ width: "100%" }}
              id="read-sensors-interval"
              select
              variant="outlined"
              label="Read Sensors Interval"
              value={readSensorsIntervalMinutes}
              helperText={readSensorsIntervalError.message}
              error={readSensorsIntervalError.hasError}
              slotProps={{
                select: {
                  native: true,
                },
              }}
              onChange={handlers.readSensorsIntervalChange}
            >
              <option key={"15"} value={"15"}>
                15 Minute
              </option>
              <option key={"30"} value={"30"}>
                30 Minute
              </option>
              <option key={"60"} value={"60"}>
                60 Minute
              </option>
            </TextField>
            <Tooltip
              enterTouchDelay={10}
              title="This is the frequency at which data is read by the device and incrementally stored on the device."
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>
          {/* Rate of Loss Alarm Algo Window */}
          <CustomToggleFieldContainer>
            <TextField
              sx={{ width: "100%" }}
              id="rate-of-loss-alarm-algo-window-seconds"
              select
              variant="outlined"
              label="Rate of Loss Alarm Algorithm Window"
              value={rateOfLossAlarmAlgoWindowSeconds}
              helperText={rateOfLossAlarmAlgoWindowSecondsError.message}
              error={rateOfLossAlarmAlgoWindowSecondsError.hasError}
              slotProps={{
                select: {
                  native: true,
                },
              }}
              onChange={handlers.rateOfLossAlarmAlgoWindowChange}
            >
              <option key={"900"} value={"900"}>
                15 Minute
              </option>
              <option key={"1800"} value={"1800"}>
                30 Minute
              </option>
              <option key={"3600"} value={"3600"}>
                60 Minute
              </option>
            </TextField>
            <Tooltip
              enterTouchDelay={10}
              title="This is the frequency at which the rate of loss alarm is triggered during an active alarm."
            >
              <IconWrapper>
                <InfoIcon
                  sx={{
                    color: colors.grayDark,
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                />
              </IconWrapper>
            </Tooltip>
          </CustomToggleFieldContainer>

          {error && (
            <FormSubmitError>
              <ErrorIcon fontSize="small" />
              {error}
            </FormSubmitError>
          )}
          <ActionButtonsContainerWithSpace>
            <Button onClick={handlers.cancel}>Cancel</Button>
            {saving ? (
              <Button disabled variant="contained">
                Saving&#8230;
              </Button>
            ) : (
              <Button onClick={handlers.save} variant="contained">
                Save
              </Button>
            )}
          </ActionButtonsContainerWithSpace>
        </InputFieldsWrapper>
      </FormContainer>
    </>
  );
};

export default AdminDeviceSettingsForm;
